import React, { FC, useEffect, useRef, useState } from "react";
import { CircularProgress } from "@mui/material";
import { Percent } from "@mui/icons-material";
import { ChevronRight, X } from "lucide-react";

import CustomModal from "../../../components/common/custom-modal/custom-modal";
import { formatCurrency, getTenantId, pxToRem } from "../../../util";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { ICartSession } from "../../../typings/interfaces/pos/cart-session";
import { CartItem } from "../../../typings/interfaces/pos/cart-item";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";
import { IPaymentModalData } from "../../../components/pos/payment-modal/payment-modal";
import Payment from "../../../components/pos/payment-modal/payment";
import PaymentErrorModal, {
  IPaymentErrorModalProps,
} from "../../../components/pos/payment-modal/PaymentErrorModal";
import CashChangeModal from "../../../components/pos/payment-modal/CashChangeModal";
import {
  BillPaymentStatus,
  BillPickupStatus,
  PaymentTypeEnum,
} from "../../../typings/enum/paymentTypes";
import {
  getBillItemIdListAction,
  getBillNoteByIdAction,
  newPaymentAction,
  pickUpItemAction,
} from "../../../redux/actions/cartAction";
import { BillNoteResponse } from "../../../typings/interfaces/responses/bill-note-response";
import { BaseResponseSingle } from "../../../typings";
import { PaymentCreateResponse } from "../../../typings/interfaces/responses/paymentResponse";
import { CartSliceAction } from "../../../redux/slice/cartSlice";
import { DiscountSliceAction } from "../../../redux/slice/discountSlice";
import { IPaymentRequest } from "../../../typings/interfaces/requests";
import { useParams } from "react-router-dom";
import useCart from "../../../hooks/useCart";
import { store } from "../../../redux/store";
import { IHalfPayment } from "../../../typings/interfaces/payment/halfPayment";
import DiscountDrawer from "./DiscountDrawer";
import ManagerCodeModal from "../../../components/common/manager-code-modal/ManagerCodeModal";
import PickupReceiptModal, {
  IPickupReceiptBillSummary,
} from "./PickupReceiptModal";
import { buildCustomizationLines, customerStatusLetter, formatFullName, formatGarmentColor, paymentStatusLetter, resolvePinTagAlias, toDisplayCase } from "../../../util/print-formatting";
import { getAllColorPinTagsAction } from "../../../redux/actions/pintagAction";
import {
  itemTotal,
  computeItemNetDues,
  computeItemBreakdown,
  computeReleasedDues,
  releasedFractionOf,
} from "./pickupReceiptMath";

interface IPickupModalProps {
  open: boolean;
  onClose: () => void;
  // When true, every invoice opens with partial-pickup selection mode
  // already switched on (used by the "Partial Pickup" entry point).
  initialPartialMode?: boolean;
}

interface IPickupCartItem extends CartItem {
  // True whenever selectedQty > 0. Kept alongside the count so every existing
  // "is this item part of the transaction" filter keeps reading naturally.
  selected: boolean;
  // Pieces of this line already handed over on an earlier pickup. A line
  // checked in as "4 x Shirt" is ONE item holding four garments, so a customer
  // can collect them across several visits.
  pickedQty: number;
  // Pieces of this line leaving in THIS transaction. Always counted from the
  // first not-yet-collected piece, since garments on one line are
  // interchangeable - only how many leave matters, not which.
  selectedQty: number;
}

/** Pieces of a line still in storage. */
const remainingPiecesOf = (ci: IPickupCartItem): number =>
  Math.max(0, (ci.qty ?? 1) - ci.pickedQty);

interface IPickupCartBill extends ICartSession {
  cartItems: IPickupCartItem[];
  billId: string;
  id: number;
  discountAmount: number;
  billAmount: number;
  paidAmount: number | null;
  totalItemCount: number;
  pickingFilterStatus: BillPickupStatus;
  paymentFilterStatus: BillPaymentStatus;
}

interface IInvoiceUiState {
  partialMode: boolean;
  payFullNow: boolean;
  expanded: boolean;
}

const GARMENT_COLOR_HEX: Record<string, string> = {
  RED: "#B03A2E",
  YELLOW: "#D9B84C",
  BROWN: "#7A4E2D",
  GREEN: "#2E7A4E",
  BLUE: "#2E5FB0",
  PURPLE: "#6B4E9E",
  BLACK: "#26262B",
  WHITE: "#F5F4EF",
  GRAY: "#8B8F8C",
  PINK: "#D98CA0",
  BURGUNDY: "#6E1F2A",
  ORANGE: "#C97A2E",
  PEACH: "#E8B98F",
  TAN: "#C9A876",
  SILVER: "#C7C9CC",
  CREAM: "#E8DCC3",
  L_BLUE: "#7FB3D9",
  L_GREEN: "#8FCB9B",
  NAVY: "#1F2E4E",
  TEAL: "#2E7A7A",
  BEIGE: "#D9C9A8",
  GOLD: "#C9A227",
  LAVENDER: "#B9A8D9",
  OLIVE: "#6B6B2E",
};

// Shared "garment – color – material – ..." description builder - used both
// for the item row's sub-label and for the itemized pickup receipt, so the
// two stay in sync instead of drifting apart.
const describeCartItem = (ci: IPickupCartItem): string =>
  [
    ci.garmentType?.name && toDisplayCase(ci.garmentType.name),
    ci.garmentColor?.map((c) => formatGarmentColor(String(c))).join(", "),
    ci.materialType?.name && toDisplayCase(ci.materialType.name),
    ci.texture && toDisplayCase(ci.texture),
    ci.garmentStyle?.name && toDisplayCase(ci.garmentStyle.name),
  ]
    .filter(Boolean)
    .join(" – ");

const ModalTopBar: FC<{
  title: string;
  subtitle?: React.ReactNode;
  onClose: () => void;
}> = ({ title, subtitle, onClose }) => (
  <div className="flex items-start justify-between px-8 pb-4 pt-7">
    <div>
      <h2 className="text-[length:var(--fs-2xl)] font-extrabold tracking-tight text-[#4D4D4D]">{title}</h2>
      {subtitle && (
        <div className="mt-1 text-[length:var(--fs-base)] font-medium text-[#4D4D4D]/60">{subtitle}</div>
      )}
    </div>
    <button
      type="button"
      aria-label="Close"
      onClick={onClose}
      className="flex h-9 w-9 flex-none items-center justify-center rounded-full border border-[#D9D9D9] text-[#4D4D4D] transition hover:bg-[#D9D9D9]/35"
    >
      <X size={18} />
    </button>
  </div>
);

const ModeToggle: FC<{
  on: boolean;
  title: string;
  subtitle: string;
  onClick: () => void;
  disabled?: boolean;
}> = ({ on, title, subtitle, onClick, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    className={`flex min-h-[3.1rem] min-w-[230px] flex-1 items-center gap-2.5 rounded-xl border bg-white px-3 py-2 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
      on ? "border-[#018752]" : "border-[#D9D9D9]"
    }`}
  >
    <div>
      <b className="block text-[length:var(--fs-base)] font-semibold text-[#4D4D4D]">{title}</b>
      <span className="text-[length:var(--fs-xs)] text-[#4D4D4D]/60">{subtitle}</span>
    </div>
    <span
      className={`relative ml-auto h-[1.6rem] w-[2.85rem] flex-none rounded-full transition-colors ${
        on ? "bg-[#018752]" : "bg-[#D9D9D9]"
      }`}
    >
      <span
        className={`absolute top-[3px] h-[1.2rem] w-[1.2rem] rounded-full bg-white shadow transition-all ${
          on ? "left-[1.45rem]" : "left-[3px]"
        }`}
      />
    </span>
  </button>
);

const statusToneClasses: Record<string, string> = {
  active: "bg-[#018752]/10 text-[#018752] border-[#018752]/20",
  warning: "bg-[#B86A00]/10 text-[#9A5900] border-[#B86A00]/20",
  idle: "bg-[#D9D9D9]/60 text-[#4D4D4D] border-[#D9D9D9]",
};

interface IInvoiceCardProps {
  bill: IPickupCartBill;
  ui: IInvoiceUiState;
  // Item ids the backend's per-item paid-status field (BillItemWithPaymentStatusDto.isPaid)
  // reports as fully covered by an item-scoped payment - independent of
  // lockedItemIds (pickup status), so a picked-up item and a paid item can be
  // told apart in the item list.
  paidItemIds: Set<string>;
  cardTotal: number;
  // Net-due for the SELECTED items on this bill (post-discount, minus
  // whatever's already been paid for those specific items) - EXCEPT once
  // every item on the bill is already picked up (nothing left to select),
  // in which case this is the bill's true total outstanding due instead
  // (see effectiveDueForBill in the parent), since a fully-locked bill can
  // never have any selected items to scope to. Computed by the parent
  // since it needs the discount/paid-status context.
  dueForSelected: number;
  // The bill's TRUE total outstanding due (every item, selection-
  // independent - see trueDueForBill in the parent), used ONLY to decide
  // whether the "Pay Full Invoice Now" toggle is enabled and what its
  // subtitle says. Unlike dueForSelected, this never collapses to 0 just
  // because every item happens to be deselected, so the toggle can always
  // be turned back on for a bill that genuinely still owes money -
  // regardless of current selection state.
  trueDueForBill: number;
  onToggleExpand: () => void;
  onHeaderToggle: (checked: boolean) => void;
  onTogglePartial: () => void;
  onTogglePayFull: () => void;
  // pieceIndex is 0-based across the whole line, collected pieces included.
  onTogglePiece: (itemId: string, pieceIndex: number) => void;
}

const InvoiceCard: FC<IInvoiceCardProps> = ({
  bill,
  ui,
  paidItemIds,
  cardTotal,
  dueForSelected,
  trueDueForBill,
  onToggleExpand,
  onHeaderToggle,
  onTogglePartial,
  onTogglePayFull,
  onTogglePiece,
}) => {
  const checkboxRef = useRef<HTMLInputElement>(null);

  const dueForBill = dueForSelected;

  // Everything on this card is counted in GARMENTS, not line items: a line
  // checked in as "4 x Shirt" is four pieces that can leave separately, so a
  // line-item count would misreport both the header and the selection.
  const totalPieces = bill.cartItems.reduce((s, ci) => s + ci.qty, 0);
  const remainingPieces = bill.cartItems.reduce(
    (s, ci) => s + remainingPiecesOf(ci),
    0
  );
  const selectedPieces = bill.cartItems.reduce((s, ci) => s + ci.selectedQty, 0);
  const unselectedRemainingPieces = remainingPieces - selectedPieces;

  const selectedCount = selectedPieces;
  const allSelected = remainingPieces > 0 && selectedPieces === remainingPieces;
  const someSelected = selectedPieces > 0 && !allSelected;

  // "Included in this transaction" without any items selected: driven by
  // the "Pay Full Invoice Now" toggle plus a real outstanding balance
  // (dueForBill is bill-level/effective whenever payFullNow is on or the
  // bill is fully locked - see effectiveDueForBill in the parent) -
  // regardless of whether the bill is fully locked (every item already
  // physically picked up, nothing left to select) or still has remaining,
  // not-yet-picked-up items with nothing currently selected. Pay Full
  // Invoice Now means "settle this invoice's whole balance today"
  // independent of what's selected for physical pickup.
  const paymentOnlyEligible = dueForBill > 0;
  const included = selectedPieces > 0 || (paymentOnlyEligible && ui.payFullNow);

  useEffect(() => {
    if (checkboxRef.current) checkboxRef.current.indeterminate = someSelected;
  }, [someSelected]);

  let statusTone: "idle" | "active" | "warning" = "idle";
  let statusLabel = "Not in this pickup";
  if (selectedPieces > 0) {
    if (!ui.partialMode || selectedPieces === remainingPieces) {
      statusTone = "active";
      statusLabel = "Full Pickup";
    } else {
      statusTone = "warning";
      statusLabel = `Partial · ${selectedPieces}/${remainingPieces} pieces`;
    }
  } else if (paymentOnlyEligible && ui.payFullNow) {
    statusTone = "active";
    statusLabel = "Collecting Payment";
  }

  return (
    <div
      className={`mb-3 overflow-hidden rounded-2xl border bg-white shadow-sm transition-colors ${
        included ? "border-[#018752]" : "border-[#D9D9D9] opacity-70"
      }`}
    >
      <div
        className="flex cursor-pointer flex-wrap items-center gap-3 px-4 py-3 hover:bg-[#F5F6F7]"
        onClick={onToggleExpand}
      >
        <input
          ref={checkboxRef}
          type="checkbox"
          className="h-5 w-5 flex-none accent-[#018752]"
          checked={allSelected}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => onHeaderToggle(e.target.checked)}
          aria-label={`Select all items on ${bill.billId}`}
        />
        <ChevronRight
          size={16}
          className={`flex-none text-[#4D4D4D]/50 transition-transform ${
            ui.expanded ? "rotate-90" : ""
          }`}
        />
        <div>
          <div className="text-[length:var(--fs-md)] font-bold text-[#4D4D4D]">
            {bill.billId}
          </div>
          <div className="text-[length:var(--fs-xs)] text-[#4D4D4D]/65">
            {totalPieces} pieces ·{" "}
            {formatCurrency({ amount: cardTotal, prefix: "$" })}
          </div>
        </div>
        <span
          className={`rounded-full border px-3 py-1 text-[length:var(--fs-xs)] font-bold ${statusToneClasses[statusTone]}`}
        >
          {statusLabel}
        </span>
        <div className="ml-auto flex items-center gap-4">
          <span className="whitespace-nowrap text-[length:var(--fs-base)] text-[#4D4D4D]/65">
            {included ? `${selectedPieces} pcs today` : "—"}
          </span>
          <span className="min-w-[70px] text-right text-[length:var(--fs-md)] font-bold text-[#4D4D4D]">
            {included ? formatCurrency({ amount: dueForBill, prefix: "$" }) : "—"}
          </span>
        </div>
      </div>

      {ui.expanded && (
        <div className="border-t border-[#D9D9D9] bg-[#F1F2F3] px-4 py-4">
          <div className="mb-3 flex flex-wrap gap-3">
            <ModeToggle
              on={ui.partialMode}
              title="Partial Pickup"
              subtitle="Choose which items leave today"
              onClick={onTogglePartial}
            />
            <ModeToggle
              on={ui.payFullNow}
              title="Pay Full Invoice Now"
              subtitle={
                trueDueForBill > 0
                  ? "Settle this invoice's whole balance"
                  : "Already paid in full"
              }
              onClick={onTogglePayFull}
              disabled={trueDueForBill <= 0}
            />
          </div>

          <div className="mb-2 flex items-center gap-2 px-0.5">
            <span className="rounded-full border border-[#018752]/20 bg-[#018752]/10 px-3 py-0.5 text-[length:var(--fs-xs)] font-bold text-[#018752]">
              {selectedPieces} of {remainingPieces} pieces selected
            </span>
          </div>

          {/* One row per GARMENT, not per line: a "4 x Shirt" line spreads
              into four rows labelled "1 of 4" ... "4 of 4", so the cashier can
              hand over part of it. Pieces on a line are interchangeable, so a
              row's index is only a position - the rows already collected are
              always the first ones. */}
          {bill.cartItems.flatMap((ci) => {
            const details = describeCartItem(ci);
            const chipColor =
              GARMENT_COLOR_HEX[String(ci.garmentColor?.[0])] ?? "#D9D9D9";
            const pieces = Math.max(1, ci.qty ?? 1);

            return Array.from({ length: pieces }, (_, pieceIndex) => {
              const picked = pieceIndex < ci.pickedQty;
              const selected =
                !picked && pieceIndex < ci.pickedQty + ci.selectedQty;
              const paid = paidItemIds.has(ci.itemId);
              const name = ci.garmentType?.name ?? "Item";
              // The checkbox's accessible name keeps the lowercase "item"
              // fallback the single-row version used, so an item with no
              // garment type is still addressable by the same name.
              const ariaName = ci.garmentType?.name ?? "item";

              return (
                <div
                  key={`${ci.itemId}-${pieceIndex}`}
                  onClick={() => !picked && onTogglePiece(ci.itemId, pieceIndex)}
                  className={`mb-1.5 grid grid-cols-[auto_auto_1fr_auto_auto] items-center gap-3 rounded-xl border px-3.5 py-2.5 transition-colors ${
                    picked
                      ? "cursor-default border-[#D9D9D9] bg-[#F8F8F8] opacity-60"
                      : selected
                      ? "cursor-pointer border-[#018752] bg-[#018752]/5"
                      : `border-[#D9D9D9] bg-white ${
                          ui.partialMode ? "cursor-pointer" : "cursor-default"
                        }`
                  }`}
                >
                  <input
                    type="checkbox"
                    className="h-[19px] w-[19px] flex-none accent-[#018752]"
                    checked={selected}
                    disabled={picked || !ui.partialMode}
                    onClick={(e) => e.stopPropagation()}
                    onChange={() => onTogglePiece(ci.itemId, pieceIndex)}
                    aria-label={
                      pieces > 1
                        ? `${ariaName} ${pieceIndex + 1} of ${pieces}`
                        : ariaName
                    }
                  />
                  <span
                    className="h-[13px] w-[13px] flex-none rounded"
                    style={{ background: chipColor }}
                  />
                  <div>
                    <div className="flex flex-wrap items-center gap-1.5 text-[length:var(--fs-base)] font-semibold text-[#4D4D4D]">
                      <span className={picked ? "line-through" : ""}>
                        {name}
                      </span>
                      {picked && (
                        <span className="text-[length:var(--fs-xs)] font-normal text-[#4D4D4D]/60">
                          (Picked Up)
                        </span>
                      )}
                      {paid && (
                        <span
                          className="rounded-full border border-[#018752]/25 bg-[#018752]/10 px-2 py-0.5 text-[length:var(--fs-2xs)] font-bold uppercase tracking-wide text-[#018752]"
                          title="Fully paid for on a prior item-scoped payment"
                        >
                          Paid ✓
                        </span>
                      )}
                    </div>
                    <div
                      className={`text-[length:var(--fs-xs)] text-[#4D4D4D]/60 ${
                        picked ? "line-through" : ""
                      }`}
                    >
                      {details}
                    </div>
                  </div>
                  <span className="whitespace-nowrap text-[length:var(--fs-base)] text-[#4D4D4D]/65">
                    {pieces > 1 ? `${pieceIndex + 1} of ${pieces}` : "×1"}
                  </span>
                  <span className="min-w-[70px] text-right text-[length:var(--fs-base)] font-semibold text-[#4D4D4D]">
                    {formatCurrency({
                      amount: itemTotal(ci) / pieces,
                      prefix: "$",
                    })}
                  </span>
                </div>
              );
            });
          })}

          {ui.partialMode && unselectedRemainingPieces > 0 && (
            <div className="mt-2 rounded-lg border border-[#B86A00]/20 bg-[#B86A00]/10 px-3 py-2 text-[length:var(--fs-xs)] text-[#9A5900]">
              {unselectedRemainingPieces} piece
              {unselectedRemainingPieces === 1 ? "" : "s"} stay in storage —
              this invoice stays open until they're collected.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PickupModal: FC<IPickupModalProps> = ({
  open,
  onClose,
  initialPartialMode = false,
}) => {
  const {
    cartHistorySelectedRows,
    paymentHalfDetails,
    billItemIdList,
    billItemIdListLoading,
  } = useAppSelector((state) => state.cart);

  const [preparedData, setPreparedData] = useState<IPickupCartBill[]>([]);
  const [invoiceUi, setInvoiceUi] = useState<Record<number, IInvoiceUiState>>(
    {}
  );
  const [modalType, setModalType] = useState<"pickup" | "payment">("pickup");

  const [initialRender, setInitialRender] = useState(true);

  // No default - the cashier must actively pick a payment method every time.
  const [paymentType, setPaymentType] = useState<PaymentTypeEnum | null>(null);

  const [discountDrawerOpen, setDiscountDrawerOpen] = useState(false);
  const toggleDiscountDrawer = () => setDiscountDrawerOpen((prev) => !prev);

  const [managerModalOpen, setManagerModalOpen] = useState(false);

  // Itemized "leaving today vs remaining in storage" receipt, shown after a
  // partial pickup (see handlePickupSubmit) instead of silently closing -
  // full pickups (nothing left on any bill) keep today's existing quiet-close
  // behaviour untouched.
  const [pickupReceiptBills, setPickupReceiptBills] = useState<
    IPickupReceiptBillSummary[]
  >([]);
  const [showPickupReceipt, setShowPickupReceipt] = useState(false);

  // Shown instead of crashing when a payment attempted from this modal comes
  // back FAILED (e.g. the bill was settled behind the UI's back, or a
  // backend validation guard rejects it) - mirrors payment-modal.tsx's
  // handling of the same BaseResponseSingle shape.
  const [showPaymentErrorModal, setShowPaymentErrorModal] = useState(false);
  const [paymentErrorData, setPaymentErrorData] = useState<Omit<
    IPaymentErrorModalProps,
    "open" | "onClose" | "onRetry"
  > | null>(null);

  // Cash-tendered confirmation, shown once the whole transaction's target is
  // reached IF the completing tender was CASH - reuses payment-modal.tsx's
  // own CashChangeModal (the app's established "payment complete" format)
  // rather than the bare "Payment successful" snackbar this flow previously
  // ended on, which gave the cashier no lasting record of the change to hand
  // back. Captured via refs (mirrors payment-modal.tsx's cashTenderedRef/
  // cashBillRef exactly) because paymentType/the Payment component's own
  // selection resets between each stacked payment in a split transaction, so
  // by the time the completion effect below fires there's nothing left in
  // state to say what the LAST leg's method or tendered amount actually was.
  const [showCashChangeModal, setShowCashChangeModal] = useState(false);
  // Shaped like payment-modal.tsx's own confirmation data: the pickup screen
  // now confirms EVERY payment method, not just cash, so each method's own
  // detail (cheque number, outstanding balance, prior store-credit balance)
  // has somewhere to go.
  const [cashChangeData, setCashChangeData] = useState<{
    paymentType?: PaymentTypeEnum;
    billAmount: number;
    tenderedAmount?: number;
    changeAmount?: number;
    isStoreCredit?: boolean;
    checkNumber?: string;
    outstandingAmount?: number;
    previousStoreCreditBalance?: number;
  } | null>(null);
  const lastPaymentTypeRef = useRef<PaymentTypeEnum | null>(null);
  const cashTenderedRef = useRef(0);
  const cashBillRef = useRef(0);
  const checkNumberRef = useRef<string | undefined>(undefined);
  const cashIsStoreCreditRef = useRef(false);

  const paidAmount = paymentHalfDetails.reduce(
    (acc, item) => acc + item.halfPaymentAmount,
    0
  );

  const [drawerHost, setDrawerHost] = useState<HTMLDivElement | null>(null);

  const { getSessionTotalBySession, getSessionDiscountBySession } = useCart();

  const snackbar = useSnackbarContext();
  const tenantId = getTenantId();
  const dispatch = useAppDispatch();
  const params = useParams();
  const customerId = params["id"];
  const { user } = useAppSelector((state) => state.auth);
  const { customer } = useAppSelector((state) => state.customer);

  useEffect(() => {
    dispatch(CartSliceAction.clearSession());
    dispatch(DiscountSliceAction.resetDiscount());
  }, []);

  // Needed to resolve the pin tag's 2-letter alias for the pickup receipt
  // (resolvePinTagAlias) - nothing else in this flow loads the pin tag color
  // catalog.
  useEffect(() => {
    if (open && !store.getState().pintag.getColorPintags?.body?.length) {
      dispatch(getAllColorPinTagsAction());
    }
  }, [open, dispatch]);

  useEffect(() => {
    if (!open) setDiscountDrawerOpen(false);
  }, [open]);

  useEffect(() => {
    if (!open) setPaymentType(null);
  }, [open]);

  // PickupModal stays mounted for the whole life of the customer-history
  // page (only CustomModal's visibility toggles), so these refs would
  // otherwise keep the LAST completed payment's method/tendered figures
  // forever - letting a later effect run re-show that payment's
  // CashChangeModal, or a later transaction inherit its method. Clear them
  // on every close; the figures actually on screen were already copied into
  // `cashChangeData` state before this runs.
  useEffect(() => {
    if (!open) {
      lastPaymentTypeRef.current = null;
      cashTenderedRef.current = 0;
      cashBillRef.current = 0;
      checkNumberRef.current = undefined;
      cashIsStoreCreditRef.current = false;
    }
  }, [open]);

  // on page unmount, clear the session
  useEffect(() => {
    return () => {
      dispatch(CartSliceAction.clearSession());
      dispatch(DiscountSliceAction.resetDiscount());
    };
  }, []);

  // Garments this screen has already handed over, keyed by bill + item, kept
  // for as long as the POS session lives.
  //
  // The bill's item list is re-read after every pickup, but that read is not
  // instant and the cashier's selection is cleared in between (which rebuilds
  // every card from scratch, with no memory of what just left). Until the
  // server's own pickedQty catches up, this is what stops the screen offering
  // a garment the customer is already carrying out of the shop - handing the
  // same piece over twice is the one mistake this screen must not make.
  const handedOverRef = useRef<Record<string, number>>({});
  const handedOverKey = (billId: number, hashId: string) =>
    `${billId}:${hashId}`;

  const prepareCartItem = (
    cartItem: CartItem,
    billId: number
  ): IPickupCartItem => {
    const apiItem = billItemIdList
      .find((billItem) => billItem.billId === billId)
      ?.billItemIdList?.find((item) => item.hashId === cartItem.itemId);

    const pieces = Math.max(1, cartItem.qty ?? 1);
    // pickedQty is what the API now reports per line. Falling back to the
    // pickup status keeps this working against an API that predates
    // partial-quantity pickup, where a line was all-or-nothing.
    const pickedQty = Math.min(
      pieces,
      Math.max(
        apiItem?.pickedQty ??
          (apiItem?.pickupStatus === BillPickupStatus.PICKED_UP ? pieces : 0),
        handedOverRef.current[handedOverKey(billId, cartItem.itemId)] ?? 0
      )
    );
    const remaining = pieces - pickedQty;

    // Default: everything still in storage leaves today - the same starting
    // point as before, now counted in pieces.
    return {
      ...cartItem,
      pickedQty,
      selectedQty: remaining,
      selected: remaining > 0,
    };
  };

  // PV-TASK-66: `cartHistorySelectedRows`/`billItemIdList` can both be
  // reassigned to a NEW array reference mid-transaction while this modal is
  // already open - e.g. a successful payment (`newPaymentAction.fulfilled`)
  // sets `refreshCustomerHistory`, which `customer-history-table.tsx`'s own
  // effect answers by re-dispatching `setCartHistorySelectedRows` with a
  // freshly-fetched (but reference-distinct) array, even when its CONTENTS
  // are unchanged. That alone used to rerun this effect and rebuild every
  // bill's `cartItems` from scratch via `prepareCartItem`, which has no
  // memory of anything the cashier manually toggled - silently re-selecting
  // an item the cashier had deliberately unchecked (confirmed live: deselect
  // the only item on a bill, pay part of it via Cash - which triggers this
  // exact refetch - and the item's `selected` flag was back to `true` by the
  // time the next payment's `handlePickupSubmit` ran, wrongly marking the
  // whole bill picked up despite nothing ever being selected for pickup).
  //
  // Fix: carry forward each item's own prior `selected` value from the
  // CURRENT `preparedData` (this session's own state, not server data) when
  // rebuilding, instead of always recomputing it from scratch - but only for
  // an item that isn't actually locked right now. An item that just became
  // genuinely PICKED_UP (physically, by this or a concurrent transaction)
  // must still unconditionally lock/uncheck regardless of any stale prior
  // selection - `prepareCartItem`'s own pickup-status-driven `selected` is
  // authoritative in that direction. A bill/item that has no prior entry in
  // `preparedData` yet (first time this useEffect ever saw it) has nothing
  // to carry forward, so it keeps today's default ("selected unless already
  // picked up").
  const mergeWithPriorSelection = (
    prepared: IPickupCartItem,
    prevItem: IPickupCartItem | undefined
  ): IPickupCartItem => {
    if (!prevItem) return prepared;
    // Now locked (every piece physically picked up) - always wins, even over a
    // prior selection from before it was picked up.
    if (!prepared.selected) return prepared;
    // Still has pieces in storage - preserve whatever the cashier last set,
    // rather than clobbering it back to the "all selected by default"
    // recomputation. Clamped to what's actually left, since a concurrent
    // pickup may have collected some of the pieces the cashier had chosen.
    const carried = Math.min(
      prevItem.selectedQty ?? 0,
      remainingPiecesOf(prepared)
    );
    return { ...prepared, selectedQty: carried, selected: carried > 0 };
  };

  useEffect(() => {
    if (!billItemIdListLoading) {
      setPreparedData((prevPreparedData) => {
        const prevBillsById = new Map(
          prevPreparedData.map((bill) => [bill.id, bill])
        );
        return cartHistorySelectedRows.map((rowBill) => {
          const temp: ICartSession = JSON.parse(
            rowBill.billString
          ) as ICartSession;
          const prevBill = prevBillsById.get(rowBill.id);
          const prevItemsById = new Map(
            (prevBill?.cartItems ?? []).map((ci) => [ci.itemId, ci])
          );
          const cartItems: IPickupCartItem[] = temp.cartItems.map(
            (cartItem) =>
              mergeWithPriorSelection(
                prepareCartItem(cartItem, rowBill.id),
                prevItemsById.get(cartItem.itemId)
              )
          );
          const temp2: IPickupCartBill = temp as IPickupCartBill;
          temp2.cartItems = cartItems;
          temp2.billId = rowBill.billId;
          temp2.id = rowBill.id;
          temp2.discountAmount = rowBill.discountAmount ?? 0;
          temp2.billAmount = rowBill.billAmount;
          temp2.paidAmount = rowBill.paidAmount;
          temp2.totalItemCount = rowBill.totalItemCount;
          temp2.pickingFilterStatus =
            rowBill.pickingFilterStatus as BillPickupStatus;
          temp2.paymentFilterStatus =
            rowBill.paymentFilterStatus as BillPaymentStatus;
          return temp2;
        });
      });
    }
  }, [billItemIdList, cartHistorySelectedRows, billItemIdListLoading]);

  // keep per-invoice UI state (toggles/expansion) in sync with preparedData,
  // preserving existing entries across rebuilds so toggles don't reset
  useEffect(() => {
    setInvoiceUi((prev) => {
      const next: Record<number, IInvoiceUiState> = {};
      preparedData.forEach((bill) => {
        const due = bill.billAmount - (bill.paidAmount ?? 0);
        next[bill.id] = prev[bill.id] ?? {
          partialMode: initialPartialMode,
          payFullNow: due > 0,
          // Every invoice card starts collapsed, regardless of how many
          // bills are in this pickup - the chevron (toggleExpand) is the
          // only way to open one, so the modal never opens already showing
          // a bill's full item list unasked.
          expanded: false,
        };
      });
      return next;
    });
  }, [preparedData, initialPartialMode]);

  // Re-opening the modal (e.g. via the "Partial Pickup" entry point, or the
  // normal "Pay and Pickup" CTA right after it) without the underlying
  // invoice selection changing skips the block above entirely - preparedData/
  // invoiceUi already have entries for these bills from a previous open, so
  // nothing would resync partialMode. The modal never unmounts between opens
  // (only CustomModal's visibility toggles), so invoiceUi persists across
  // close/reopen by design - resync partialMode to initialPartialMode on
  // every rising edge of `open`, in both directions, so a prior
  // Partial-Pickup open doesn't leak partialMode=true into a later plain
  // Pay-and-Pickup open of the same bill (and vice versa).
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      setInvoiceUi((prev) => {
        const next: Record<number, IInvoiceUiState> = {};
        Object.keys(prev).forEach((key) => {
          const id = Number(key);
          next[id] = { ...prev[id], partialMode: initialPartialMode };
        });
        return next;
      });
    }
    wasOpenRef.current = open;
  }, [open, initialPartialMode]);

  // Item ids the backend reports as fully covered by an item-scoped payment
  // (BillItemWithPaymentStatusDto.isPaid) - independent of pickup status, so a
  // picked-up-but-unpaid item and a paid item render differently in the list.
  const paidItemIdsFor = (billId: number): Set<string> =>
    new Set(
      billItemIdList
        .find((billItem) => billItem.billId === billId)
        ?.billItemIdList?.filter((item) => item.isPaid)
        .map((item) => item.hashId) ?? []
    );

  const toggleExpand = (billId: number) => {
    setInvoiceUi((prev) => ({
      ...prev,
      [billId]: { ...prev[billId], expanded: !prev[billId]?.expanded },
    }));
  };

  const togglePayFullNow = (billId: number) => {
    setInvoiceUi((prev) => ({
      ...prev,
      [billId]: { ...prev[billId], payFullNow: !prev[billId]?.payFullNow },
    }));
  };

  const togglePartialMode = (billId: number) => {
    const cur = invoiceUi[billId];
    if (!cur) return;
    if (cur.partialMode) {
      // turning partial mode off = force full pickup (every piece still in
      // storage leaves today)
      setPreparedData((prev) =>
        prev.map((bill) =>
          bill.id !== billId
            ? bill
            : {
                ...bill,
                cartItems: bill.cartItems.map((ci) => {
                  const qty = remainingPiecesOf(ci);
                  return { ...ci, selectedQty: qty, selected: qty > 0 };
                }),
              }
        )
      );
    }
    setInvoiceUi((prev) => ({
      ...prev,
      [billId]: { ...prev[billId], partialMode: !prev[billId]?.partialMode },
    }));
  };

  /**
   * Clicking the nth still-in-storage piece of a line selects exactly n
   * pieces of it; clicking the last selected piece drops back to n-1. Pieces
   * on one line are interchangeable garments, so the selection is a COUNT
   * taken from the top rather than a set of individual rows - which also
   * keeps it in step with what the API stores (a quantity per item id).
   */
  const togglePieceSelect = (
    billId: number,
    cartItemId: string,
    pieceIndex: number
  ) => {
    if (!invoiceUi[billId]?.partialMode) return;
    setPreparedData((prev) =>
      prev.map((bill) =>
        bill.id !== billId
          ? bill
          : {
              ...bill,
              cartItems: bill.cartItems.map((ci) => {
                if (ci.itemId !== cartItemId) return ci;
                const offset = pieceIndex - ci.pickedQty;
                // Already collected on an earlier visit - not selectable.
                if (offset < 0 || offset >= remainingPiecesOf(ci)) return ci;
                const nextQty = offset < ci.selectedQty ? offset : offset + 1;
                return { ...ci, selectedQty: nextQty, selected: nextQty > 0 };
              }),
            }
      )
    );
  };

  const headerToggle = (billId: number, checked: boolean) => {
    setPreparedData((prev) =>
      prev.map((bill) =>
        bill.id !== billId
          ? bill
          : {
              ...bill,
              cartItems: bill.cartItems.map((ci) => {
                const qty = checked ? remainingPiecesOf(ci) : 0;
                return { ...ci, selectedQty: qty, selected: qty > 0 };
              }),
            }
      )
    );
    if (checked) {
      setInvoiceUi((prev) => ({
        ...prev,
        [billId]: { ...prev[billId], expanded: true },
      }));
    }
  };

  // bill.billAmount/paidAmount are already net of bill.discountAmount (the
  // discount persisted on the bill, e.g. via the edit-invoice flow). Until a
  // bill has one saved, fall back to whatever discount is currently selected
  // in the checkout Discount modal so it shows up here without requiring the
  // bill to be edited first.
  const persistedDiscountFor = (bill: IPickupCartBill) =>
    bill.discountAmount ?? 0;
  const effectiveDiscountFor = (bill: IPickupCartBill) =>
    persistedDiscountFor(bill) > 0
      ? persistedDiscountFor(bill)
      : getSessionDiscountBySession(bill);

  // Per-item CLEARED-only paid amount (BillItemWithPaymentStatusDto.
  // clearedPaidAmount) - unlike the all-status paidAmount/isPaid pair used
  // by paidItemIdsFor and the per-piece "Picked Up" state and the "PAID"
  // badge (which
  // correctly count NOT_CLEARED MONTHLY/LATER pay-on-credit entries as
  // sufficient to release an item for pickup), this EXCLUDES those, so it
  // reflects money actually collected. Use this for any money math (Amount
  // Due, netDue, breakdown) so an item settled via pay-on-credit doesn't
  // falsely read as fully paid when real cash is still owed.
  const billItemClearedPaidAmount = (
    bill: IPickupCartBill,
    itemId: string
  ): number =>
    billItemIdList
      .find((b) => b.billId === bill.id)
      ?.billItemIdList?.find((bi) => bi.hashId === itemId)
      ?.clearedPaidAmount ?? 0;

  // Per-item net-due for every item on the bill (post-discount, minus
  // whatever's already been paid specifically for that item) - the actual
  // math lives in pickupReceiptMath.ts (shared, unit-tested, and reused
  // as-is by the itemized partial-pickup receipt below) so this screen and
  // the receipt can never drift out of sync with each other.
  const netDuesFor = (
    bill: IPickupCartBill,
    billPaidAmountOverride?: number
  ) =>
    computeItemNetDues(
      bill.cartItems,
      effectiveDiscountFor(bill),
      billPaidAmountOverride ?? bill.paidAmount ?? 0,
      (itemId) => billItemClearedPaidAmount(bill, itemId)
    );

  // Each line's value is split evenly across its pieces (the client's rule),
  // so one of four shirts carries a quarter of that line - upcharges,
  // alterations and spot charges included. Payments already made against the
  // line are then applied to the pieces released, rather than the line's
  // post-payment net due being pro-rated a second time (see
  // computeReleasedDues) - otherwise a bill part-paid at an earlier pickup
  // could never be settled when the customer came back for the rest.
  const releasedDuesFor = (
    bill: IPickupCartBill,
    billPaidAmountOverride?: number
  ) =>
    computeReleasedDues(
      bill.cartItems,
      effectiveDiscountFor(bill),
      billPaidAmountOverride ?? bill.paidAmount ?? 0,
      (itemId) => billItemClearedPaidAmount(bill, itemId)
    );

  const dueForSelectedItems = (bill: IPickupCartBill): number =>
    releasedDuesFor(bill).reduce((acc, x) => acc + x.releasedDue, 0);

  // Total remaining (not-yet-picked-up) piece count for a bill - 0 once
  // every item on it has already been physically picked up, i.e. there's
  // nothing left to select. Mirrors InvoiceCard's own `remainingPieces`
  // calc; needed here too since the payableBills/summary-panel math below
  // also has to tell fully-locked bills apart from ones with items left.
  const remainingPiecesFor = (bill: IPickupCartBill): number =>
    bill.cartItems.reduce((s, ci) => s + remainingPiecesOf(ci), 0);

  // Whether every item on this bill is already picked up - when true,
  // nothing can ever be `selected` on it, so dueForSelectedItems always
  // reads exactly 0 regardless of what's actually still owed.
  const isFullyPickedUp = (bill: IPickupCartBill): boolean =>
    remainingPiecesFor(bill) === 0;

  // Bill-level "true outstanding due" that does NOT depend on item
  // selection - the same per-item discount- and prior-payment-aware
  // net-due split as dueForSelectedItems (netDuesFor/computeItemNetDues),
  // just summed across EVERY item on the bill instead of only the selected
  // ones. Used as the fallback for fully-picked-up bills, where
  // dueForSelectedItems can never reflect the real balance since nothing on
  // them is ever selectable.
  const trueDueForBill = (bill: IPickupCartBill): number =>
    netDuesFor(bill).reduce((acc, x) => acc + x.netDue, 0);

  // The due figure to actually use for a bill's toggle/CTA/summary math:
  // selection-scoped as before for bills that still have pickable items,
  // but the bill's TRUE total due once either (a) every item on it is
  // already picked up (previously the only case handled here - the fix for
  // invoices that are fully "Picked Up" but still owe money, since nothing
  // on such a bill is ever selectable, so dueForSelectedItems was always
  // exactly 0), or (b) "Pay Full Invoice Now" is on for it. (b) is the fix
  // for a bill that still has remaining, not-yet-picked-up items: once the
  // cashier says the whole invoice is being settled today, the due amount
  // must stay pinned to the bill's real balance and NOT keep changing as
  // items are (de)selected under Partial Pickup - selection should only
  // ever drive what's physically handed over, never the money. This is a
  // pure function of the current payFullNow toggle state, so it doesn't
  // matter whether Partial Pickup or Pay Full Invoice Now was switched on
  // first.
  const effectiveDueForBill = (bill: IPickupCartBill): number =>
    isFullyPickedUp(bill) || invoiceUi[bill.id]?.payFullNow
      ? trueDueForBill(bill)
      : dueForSelectedItems(bill);

  // Same per-item split as netDuesFor, but exposing the gross/discount/paid
  // components too (not just netDue) - used to make the Subtotal/Discounts/
  // Paid-so-far summary panel selection-scoped, the same way Amount Due
  // (dueForSelectedItems) and the itemized receipt already are, without
  // recomputing the discount/payment apportionment a third time.
  const breakdownFor = (
    bill: IPickupCartBill,
    billPaidAmountOverride?: number
  ) =>
    computeItemBreakdown(
      bill.cartItems,
      effectiveDiscountFor(bill),
      billPaidAmountOverride ?? bill.paidAmount ?? 0,
      (itemId) => billItemClearedPaidAmount(bill, itemId)
    );

  // Resolves the numeric BillItem ids for a bill's cart items, reusing the
  // same hashId -> billItemId lookup handlePickupSubmit already did (now
  // also used before the payment call, not just the pickup call).
  // `scope` controls WHICH cart items get resolved:
  //  - "selected" (default): only the currently-selected items - what
  //    handlePickupSubmit uses to mark items physically picked up, and what
  //    a payment covering only a partial selection should be scoped to.
  //  - "all": every item on the bill, regardless of selection - used for
  //    the payFullNow payment case below, where the amount being charged
  //    (effectiveDueForBill, pinned to trueDueForBill) already covers the
  //    bill's FULL balance, not just the selected items' share. Keeping
  //    itemIds selection-scoped in that case would decouple the payment
  //    amount from its item attribution: the server would apportion the
  //    whole payment onto only the selected items (over-satisfying them)
  //    while leaving deselected items looking unpaid, so a customer who
  //    already paid in full could be charged again when they return for
  //    the remaining items. This never affects what's physically picked up
  //    today - only which itemIds accompany the PAYMENT request.
  const resolveBillItemIds = (
    bill: IPickupCartBill,
    scope: "selected" | "all" = "selected"
  ): number[] => {
    const billItemList = billItemIdList.find(
      (billItem) => billItem.billId === bill.id
    );
    if (!billItemList) {
      throw new Error(
        "Bill item List not found. Possibly this is an old bill"
      );
    }
    return bill.cartItems
      .filter((cartItem) => scope === "all" || cartItem.selectedQty > 0)
      .map((cartItem) => {
        const billItemId = billItemList.billItemIdList?.find(
          (billItem) => billItem.hashId === cartItem.itemId
        )?.billItemId;

        if (!billItemId) {
          throw new Error(
            "Bill item ID not found. Possibly this is an old bill"
          );
        }
        return billItemId;
      });
  };

  // Back-compat alias for the (far more common) selected-only resolution -
  // keeps every existing call site unchanged.
  const resolveSelectedBillItemIds = (bill: IPickupCartBill): number[] =>
    resolveBillItemIds(bill, "selected");

  /**
   * How many pieces of each selected line are leaving, keyed by the numeric
   * BillItem id the pickup API expects. Sent alongside itemIds so the server
   * records "one of the four shirts", not the whole line.
   */
  const resolveSelectedPieceQuantities = (
    bill: IPickupCartBill
  ): Record<number, number> => {
    const billItemList = billItemIdList.find(
      (billItem) => billItem.billId === bill.id
    );
    const quantities: Record<number, number> = {};

    bill.cartItems
      .filter((cartItem) => cartItem.selectedQty > 0)
      .forEach((cartItem) => {
        const billItemId = billItemList?.billItemIdList?.find(
          (billItem) => billItem.hashId === cartItem.itemId
        )?.billItemId;
        if (billItemId) {
          quantities[billItemId] = Math.min(
            cartItem.selectedQty,
            remainingPiecesOf(cartItem)
          );
        }
      });

    return quantities;
  };

  const billsWithSelection = preparedData.filter((bill) =>
    bill.cartItems.some((ci) => ci.selected)
  );
  const isAnyPickupHappening = billsWithSelection.length > 0;

  // Bills that should count toward the summary panel/CTA dollar figure this
  // transaction - either because at least one item is selected on them (the
  // usual physical-pickup case), or because there's a real outstanding
  // balance and "Pay Full Invoice Now" is on for them (the payment-only
  // case this fix enables) - regardless of whether the bill is fully
  // locked (every item already picked up, nothing left to select) or still
  // has remaining, not-yet-picked-up items with nothing currently
  // selected. Pay Full Invoice Now means "settle this invoice's whole
  // balance today" independent of what's selected for physical pickup.
  const billsIncludedInSummary = preparedData.filter((bill) => {
    if (bill.cartItems.some((ci) => ci.selected)) return true;
    return !!invoiceUi[bill.id]?.payFullNow && trueDueForBill(bill) > 0;
  });

  // The set of bills that actually have money to collect THIS transaction,
  // and the total amount due across them - this used to be narrowly scoped
  // to only bills with "Pay Full Invoice Now" on
  // (`preparedData.filter(bill => invoiceUi[bill.id]?.payFullNow)`), which
  // silently dropped the ordinary Partial Pickup case (payFullNow off, one
  // or more items selected, a real per-item balance due): `amountDue` would
  // read 0 even though the CTA/summary panel correctly showed a nonzero due
  // (via the now-removed, separately-scoped `selectionScopedAmountDue`),
  // causing `onSubmit` to skip the payment step entirely and
  // `handleOnPaymentSubmit` to build an empty payment list even if the
  // payment step were somehow reached. `payableBills`/`amountDue` now share
  // the exact same transaction-inclusion scope as `billsIncludedInSummary`
  // (used by the summary panel and CTA dollar figure), filtered down to
  // bills that actually have a nonzero due - so every consumer of
  // `payableBills`/`amountDue` (nothingToDo/ctaDisabled, ctaLabel, onSubmit,
  // handleOnPaymentSubmit's isFullPaymentDone guard and initialHalfPayments
  // construction, and the pickupAmount prop into <Payment>) now agrees with
  // what's actually displayed.
  const payableBills = billsIncludedInSummary.filter(
    (bill) => effectiveDueForBill(bill) > 0
  );
  const amountDue = payableBills.reduce(
    (tot, bill) => tot + effectiveDueForBill(bill),
    0
  );

  const piecesToday = preparedData.reduce(
    (tot, bill) =>
      tot + bill.cartItems.reduce((s, ci) => s + ci.selectedQty, 0),
    0
  );
  const piecesRemainingTotal = preparedData.reduce(
    (tot, bill) => tot + remainingPiecesFor(bill),
    0
  );

  // Selection-scoped Subtotal/Discounts/Paid-so-far for the summary panel -
  // sums only the SELECTED items' gross/discount-share/paid-share (via
  // breakdownFor/pickupReceiptMath.computeItemBreakdown), matching the
  // Amount Due figure right next to it and the itemized post-submit
  // receipt for the same selection. When every item on every bill is
  // selected (the common, non-partial case), this sums back to exactly the
  // same totals the old bill.billAmount/discountAmount/paidAmount-based
  // math produced - see computeItemNetDues's "full-bill selection... no
  // regression" test in pickupReceiptMath.test.ts.
  //
  // For a payment-only bill with zero items selected (see
  // billsIncludedInSummary above - whether every item on it is already
  // picked up, or it simply has "Pay Full Invoice Now" on with nothing
  // currently selected) there's nothing selected to filter down to - the
  // whole bill's breakdown is used instead, since every item on it is being
  // paid off in one go. The same full breakdown is also used whenever "Pay
  // Full Invoice Now" is on with a nonzero selection, for the same reason
  // effectiveDueForBill pins to trueDueForBill above: once the whole
  // invoice is being settled today, these lines must stay pinned to the
  // bill's real totals and not keep shifting as items are (de)selected
  // under Partial Pickup.
  const selectionScopedTotals = billsIncludedInSummary.reduce(
    (acc, bill) => {
      const hasSelection = bill.cartItems.some((ci) => ci.selected);
      const breakdown = breakdownFor(bill);
      const useFullBreakdown =
        !hasSelection || !!invoiceUi[bill.id]?.payFullNow;
      // Partial selections price the pieces released so far (collected plus
      // selected now) and then subtract everything already paid on the line -
      // the same basis as Amount Due beside it and the itemized receipt after
      // submit, so Subtotal - Discounts - Paid still lands exactly on Amount
      // Due. Weight 1 per line when the whole invoice is in play.
      const fractionFor = (itemId: string) => {
        if (useFullBreakdown) return 1;
        const item = bill.cartItems.find((ci) => ci.itemId === itemId);
        return item ? releasedFractionOf(item) : 0;
      };
      return {
        subtotal:
          acc.subtotal +
          breakdown.reduce((s, x) => s + x.grossTotal * fractionFor(x.itemId), 0),
        totalDiscounts:
          acc.totalDiscounts +
          breakdown.reduce(
            (s, x) => s + x.discountShare * fractionFor(x.itemId),
            0
          ),
        totalPaid:
          acc.totalPaid +
          breakdown.reduce(
            (s, x) => s + (fractionFor(x.itemId) > 0 ? x.paidAmount : 0),
            0
          ),
      };
    },
    { subtotal: 0, totalDiscounts: 0, totalPaid: 0 }
  );
  const { subtotal, totalDiscounts, totalPaid } = selectionScopedTotals;

  const allDoneAlready =
    preparedData.length > 0 &&
    preparedData.every(
      (bill) =>
        bill.paymentFilterStatus === BillPaymentStatus.FULLY_PAID &&
        bill.pickingFilterStatus === BillPickupStatus.PICKED_UP
    );
  // A bill that's fully picked up but still has a real outstanding balance
  // used to be unconditionally blocked here (mixedGuard) - that's exactly
  // the "collect payment on an already-picked-up invoice" scenario this fix
  // enables (see effectiveDueForBill/trueDueForBill above), so it's
  // intentionally no longer a blocking condition, including when it's
  // combined with a genuinely still-in-progress bill in the same
  // selection (one bill mid-pickup + one already-picked-up-and-unpaid) -
  // handlePickupSubmit/payableBills already process each bill
  // independently (pickup for the former, payment-only for the latter), so
  // there's nothing ambiguous left to guard against here. The two
  // remaining guards below already cover every case that should still be
  // blocked: a bill that's both picked up AND fully paid has nothing left
  // to do (allDoneAlready), and a selection with no items chosen and no
  // real balance to collect has nothing to submit (nothingToDo).
  const nothingToDo = !isAnyPickupHappening && amountDue <= 0;
  const ctaDisabled =
    preparedData.length === 0 || allDoneAlready || nothingToDo;
  // "Collect Payment" covers the payment-only case (amountDue > 0 with no
  // pieces moving today) - this branch used to read "Proceed to Pay" but
  // was unreachable before this fix (dueForSelectedItems, and so
  // amountDue, could never be > 0 without at least one selected item,
  // which would always make isAnyPickupHappening true); it's now reachable
  // via a fully-locked, already-picked-up bill with payFullNow on.
  const ctaLabel =
    amountDue > 0
      ? isAnyPickupHappening
        ? "Pay & Pickup"
        : "Collect Payment"
      : "Proceed to Pickup";

  const closeModal = () => {
    setModalType("pickup");
    onClose();
  };

  const closePickupReceipt = () => {
    setShowPickupReceipt(false);
    setPickupReceiptBills([]);
    closeModal();
  };

  const onSubmit = () => {
    if (amountDue > 0) {
      // No default payment method for most customers - the cashier must
      // make a conscious choice. EXCEPTION: a monthly-charge customer only
      // ever has Monthly enabled (see isMonthlyDisabled in payment.tsx), so
      // there's no real choice to make - pre-select it.
      const isMonthlyCustomer = (customer?.customerPayAtPref ?? []).includes(
        "MONTHLY_CHARGE"
      );
      setPaymentType(isMonthlyCustomer ? PaymentTypeEnum.MONTHLY : null);
      setModalType("payment");
    } else {
      handlePickupSubmit();
    }
  };

  const handlePickupSubmit = async () => {
    if (!tenantId) {
      snackbar.showSnackbar("Tenant ID not found", "error");
      return;
    }
    const billsToPickup = preparedData.filter((bill) =>
      bill.cartItems.some((ci) => ci.selected)
    );

    if (billsToPickup.length === 0) {
      dispatch(CartSliceAction.setRefreshCustomerHistory(true));
      // Deselect the just-completed bill(s) - same reasoning as
      // Void/RefundModal: a payment-only ("Collect Payment") completion
      // with nothing physically leaving still finished a real operation,
      // so it shouldn't linger in cartHistorySelectedRows and block the
      // toolbar or carry stale selection into the next action.
      dispatch(CartSliceAction.setCartHistorySelectedRows([]));
      closeModal();
      return;
    }

    let anyFailure = false;
    // Snapshot per-bill leaving/remaining item splits for whichever bills
    // actually succeed, so the itemized receipt reflects exactly what was
    // picked up in THIS transaction - not the whole selection if one bill in
    // a multi-bill pickup fails.
    const receiptEntries: IPickupReceiptBillSummary[] = [];
    // Raw cash tendered only means "this bill's tendered amount" when
    // there's exactly one bill in this pickup - cashTenderedRef/cashBillRef
    // track the whole transaction's combined cash (see handleOnPaymentSubmit's
    // own comment), so a multi-bill pickup (one payment split across several
    // bills) can't attribute a single tendered figure to any one bill's
    // receipt.
    const isSingleBillCashPickup =
      billsToPickup.length === 1 &&
      lastPaymentTypeRef.current === PaymentTypeEnum.CASH;

    for (const pickupBill of billsToPickup) {
      try {
        const ItemIds = resolveSelectedBillItemIds(pickupBill);
        const itemQuantities = resolveSelectedPieceQuantities(pickupBill);

        const userString = localStorage.getItem("user");
        const localUser = userString ? JSON.parse(userString) : null;

        const res = await dispatch(
          pickUpItemAction({
            billId: pickupBill.id,
            pickUpCashierId: localUser?.id?.toString(),
            itemIds: ItemIds,
            itemQuantities,
          })
        ).unwrap();

        if (!res) {
          throw new Error("Pickup failed");
        }

        // Remember what left, then ask the server for its own count. The
        // record is what keeps these garments locked in the meantime - see
        // handedOverRef.
        pickupBill.cartItems
          .filter((ci) => ci.selectedQty > 0)
          .forEach((ci) => {
            const key = handedOverKey(pickupBill.id, ci.itemId);
            handedOverRef.current[key] = Math.min(
              ci.qty,
              (handedOverRef.current[key] ?? 0) + ci.selectedQty
            );
          });
        dispatch(getBillItemIdListAction(pickupBill.id));

        const amountChargedThisTransaction =
          paymentHalfDetails.find((hp) => hp.billId === pickupBill.id)
            ?.halfPaymentAmount ?? 0;

        // Per-item discount- and prior-payment-aware net dues, reusing the
        // SAME per-item discount/paid-status split (netDuesFor, backed by
        // pickupReceiptMath.computeItemNetDues) the live selection screen
        // uses for dueForSelectedItems - not the raw undiscounted
        // itemTotal(ci) - so the receipt's numbers reconcile with what the
        // cashier actually charged.
        //
        // bill.paidAmount here already reflects THIS transaction's own
        // payment (updateHalfOrFullPayment bumps it synchronously, before
        // handlePickupSubmit ever runs), but billItemIdList's per-item
        // paidAmount is still stale until the next refetch. Feeding
        // netDuesFor the post-payment bill.paidAmount as-is would make it
        // treat this transaction's already fully item-scoped payment as an
        // unattributed bill-level payment and smear it pro-rata across
        // every item on the bill - including ones that were never part of
        // this transaction. Subtracting the just-made payment back out
        // first restores the same pre-transaction state dueForSelectedItems
        // used when the payment amount was originally computed, so each
        // item's netDue below matches exactly what was owed/charged for it.
        const netDues = netDuesFor(
          pickupBill,
          Math.max(0, (pickupBill.paidAmount ?? 0) - amountChargedThisTransaction)
        );
        const netDueByItemId = new Map(
          netDues.map((d) => [d.itemId, d.netDue])
        );
        // A line can now be split across visits, so both figures follow the
        // pieces they describe. `price` is what is still owed for those
        // pieces (the same released-pieces basis as Amount Due - see
        // computeReleasedDues), `grossPrice` their pre-discount value.
        const releasedByItemId = new Map(
          releasedDuesFor(
            pickupBill,
            Math.max(
              0,
              (pickupBill.paidAmount ?? 0) - amountChargedThisTransaction
            )
          ).map((d) => [d.itemId, d])
        );
        const grossPerPiece = (ci: IPickupCartItem, pieces: number) =>
          (ci.qty ?? 1) > 0 ? (itemTotal(ci) * pieces) / ci.qty : 0;

        const receiptLine = (
          ci: IPickupCartItem,
          pieces: number,
          price: number
        ) => ({
          description: describeCartItem(ci) || ci.garmentType?.name || "Item",
          name: ci.garmentType?.name ?? "Item",
          qty: pieces,
          price,
          grossPrice: grossPerPiece(ci, pieces),
          note: ci.note ?? undefined,
          customizations: buildCustomizationLines(ci),
        });

        const leavingItems = pickupBill.cartItems
          .filter((ci) => ci.selectedQty > 0)
          .map((ci) =>
            receiptLine(
              ci,
              ci.selectedQty,
              releasedByItemId.get(ci.itemId)?.releasedDue ??
                netDueByItemId.get(ci.itemId) ??
                itemTotal(ci)
            )
          );
        const remainingItems = pickupBill.cartItems
          .map((ci) => ({
            ci,
            pieces: remainingPiecesOf(ci) - ci.selectedQty,
          }))
          .filter(({ pieces }) => pieces > 0)
          .map(({ ci, pieces }) =>
            receiptLine(
              ci,
              pieces,
              releasedByItemId.get(ci.itemId)?.remainingDue ?? 0
            )
          );

        // Best-effort - a bill without notes, or a fetch failure, shouldn't
        // block the pickup receipt from printing everything else.
        let notes: string[] = [];
        try {
          const billNotesRes = await dispatch(
            getBillNoteByIdAction({ billId: pickupBill.id })
          );
          notes = ((billNotesRes.payload as BillNoteResponse[]) ?? []).map(
            (n) => n.note
          );
        } catch {
          notes = [];
        }

        // pickupDetails isn't on ICartSession's own type (IPickupCartBill
        // extends it), but it's present at runtime on the parsed billString,
        // same as ticket-print.ts's reprint builder relies on.
        const scheduledDueDateStr = (pickupBill as any).pickupDetails?.dueDate;

        receiptEntries.push({
          billId: pickupBill.billId,
          leavingItems,
          remainingItems,
          amountChargedThisTransaction,
          cashTendered: isSingleBillCashPickup
            ? cashTenderedRef.current
            : undefined,
          pinTagColor: pickupBill.pinTag?.color ?? undefined,
          pinTagTags: pickupBill.pinTag?.tags ?? [],
          pinTagAlias: resolvePinTagAlias(
            pickupBill.pinTag?.color,
            store.getState().pintag.getColorPintags?.body
          ),
          scheduledPickupDate: scheduledDueDateStr
            ? new Date(scheduledDueDateStr)
            : undefined,
          notes,
        });
      } catch (e: any) {
        anyFailure = true;
        snackbar.showSnackbar(e.message, "error");
      }
    }

    dispatch(CartSliceAction.setRefreshCustomerHistory(true));
    // Deselect the just-picked-up bill(s) - same reasoning as
    // Void/RefundModal: leaving them checked would block the Edit/Void/
    // Refund toolbar and carry stale selection into the next action.
    dispatch(CartSliceAction.setCartHistorySelectedRows([]));
    if (!anyFailure) {
      snackbar.showSnackbar("Pickup successful", "success");
    }

    // Every pickup ends on the itemized receipt, which is also where the
    // thermal copies are printed from - a full pickup used to close straight
    // away, which is why nothing could be printed for one. A transaction that
    // handed nothing over (payment only) still closes quietly: there is no
    // pickup to give a receipt for.
    if (receiptEntries.length > 0) {
      setPickupReceiptBills(receiptEntries);
      setShowPickupReceipt(true);
    } else {
      closeModal();
    }
  };

  useEffect(() => {
    // Absorb this effect's very first run (component mount) unconditionally -
    // it can never be a transaction this modal just completed, and Redux may
    // still be holding a previous flow's fully-paid paymentHalfDetails at
    // that instant (PickupModal's own mount effect clears them, but that
    // dispatch lands a render later). Consumed here, at the top, rather than
    // after the threshold comparison below: further down it would be spent by
    // whichever run happened to reach it first, swallowing a genuine
    // completion instead of the mount run it exists for.
    if (initialRender) {
      // this is used to load the actiontile modal only after checkout
      setInitialRender(false);
      return;
    }

    // The target is the fixed amount captured in paymentHalfDetails when the
    // payment flow started (each bill's selection-scoped net-due at that
    // time), not a live recomputation - dueForSelectedItems shrinks as
    // bill.paidAmount is bumped after each successful payment below, so it's
    // no longer a stable target to compare accumulated tendered amount against.
    const targetTotal = paymentHalfDetails.reduce(
      (tot, bill) => tot + bill.fullpaymentAmount,
      0
    );

    // An EMPTY half-payment list is not a completed transaction - it means
    // there is no payment flow in progress at all. Without this guard the
    // comparison below degenerates to `0 < 0` (false) and every such run
    // falls straight through into the completion branch. That happens for
    // real: customer-history's row click (which a checkbox tick also fires,
    // the checkbox doesn't stop propagation) dispatches clearSession(),
    // which resets paymentHalfDetails to a BRAND NEW `[]` every time - a new
    // reference, so this effect re-runs, and since PickupModal never unmounts
    // on this page, `initialRender` is long since spent and
    // lastPaymentTypeRef still holds CASH from the last payment. Result: the
    // "Payment Complete" modal re-opened off stale refs on every bill the
    // cashier selected after paying one.
    if (paymentHalfDetails.length === 0 || targetTotal <= 0) return;

    if (Number(paidAmount.toFixed(2)) < Number(targetTotal.toFixed(2))) return;

    const last = paymentHalfDetails[paymentHalfDetails.length - 1];
    const thresholdMet =
      !!last &&
      last.halfPaymentAmount >= last.fullpaymentAmount &&
      paidAmount !== 0;

    // The completing leg's own tendered/balance figures, captured by
    // handleOnPaymentSubmit right before this leg applied - shown as a
    // final confirmation the cashier must acknowledge before/alongside the
    // pickup actually closing out, same as payment-modal.tsx's own
    // checkout confirmation. Only for CASH: card/check/monthly/later/store
    // credit don't hand back physical change, so there's nothing here for
    // them to confirm.
    // Every method gets a confirmation here, matching check-in
    // (payment-modal.tsx's own switch): cash shows the change to hand back,
    // and the others show the detail that matters for their kind of
    // settlement. Previously only cash confirmed anything, so a card or
    // pay-later pickup closed with no acknowledgement at all.
    //
    // Only a payment this session actually took is confirmed:
    // lastPaymentTypeRef is set by handleOnPaymentSubmit for every method and
    // cleared when the screen closes, so a null here means this effect was
    // woken by something other than a completed tender (a leftover
    // half-payment list arriving, for instance).
    const confirmedPaymentType = lastPaymentTypeRef.current;
    if (confirmedPaymentType) {
      const outstandingAmount =
        store.getState().customer.customerOutstanding?.outstandingAmount;

      switch (confirmedPaymentType) {
        case PaymentTypeEnum.CASH:
          setCashChangeData({
            paymentType: confirmedPaymentType,
            billAmount: cashBillRef.current,
            tenderedAmount: cashTenderedRef.current,
            changeAmount: Math.max(
              0,
              cashTenderedRef.current - cashBillRef.current
            ),
            isStoreCredit: cashIsStoreCreditRef.current,
          });
          break;
        case PaymentTypeEnum.CHECK:
          setCashChangeData({
            paymentType: confirmedPaymentType,
            billAmount: cashBillRef.current,
            checkNumber: checkNumberRef.current,
          });
          break;
        case PaymentTypeEnum.MONTHLY:
        case PaymentTypeEnum.LATER:
          setCashChangeData({
            paymentType: confirmedPaymentType,
            billAmount: cashBillRef.current,
            outstandingAmount,
          });
          break;
        case PaymentTypeEnum.STORE_CREDIT:
          setCashChangeData({
            paymentType: confirmedPaymentType,
            billAmount: cashBillRef.current,
            previousStoreCreditBalance: customer?.storeCreditBalance,
          });
          break;
        default:
          setCashChangeData({
            paymentType: confirmedPaymentType,
            billAmount: cashBillRef.current,
          });
      }
      setShowCashChangeModal(true);
    }

    if (thresholdMet) {
      handlePickupSubmit();
    } else {
      closeModal();
    }
  }, [paidAmount, paymentHalfDetails]);

  const handleOnPaymentSubmit = async (data: IPaymentModalData) => {
    if (!tenantId) {
      snackbar.showSnackbar("Tenant ID not found", "error");
      return;
    }
    if (!customerId) {
      snackbar.showSnackbar("Customer ID not found", "error");
      return;
    }

    const isFullPaymentDone = amountDue <= 0;

    if (isFullPaymentDone) {
      snackbar.showSnackbar("Payment already made", "warning");
      return;
    }

    // Capture this leg's method/tendered amount now, before it applies -
    // amountDue is still the live remaining balance for THIS leg at this
    // exact point (mirrors payment-modal.tsx's identical capture, adapted
    // for Pickup's stacked/split-payment reality: the "bill total" a change
    // amount should be measured against is whatever was still due right
    // before this particular tender, not the transaction's original total).
    lastPaymentTypeRef.current = data.paymentMode;
    cashBillRef.current = amountDue;
    checkNumberRef.current = data.checkNumber;
    if (data.paymentMode === PaymentTypeEnum.CASH) {
      cashTenderedRef.current = data.rawPaidAmount ?? data.paidAmount;
      cashIsStoreCreditRef.current = data.isStoreCredit ?? false;
    }

    // A half-payment list belongs to ONE payment flow: it carries each bill's
    // target (the due when that flow started) and how much of it has been
    // tendered so far, and makePayment skips any bill whose target is already
    // met. A list left over from an EARLIER transaction on the same bill -
    // the customer paid for the garments they collected, then came back for
    // the rest - therefore silently skipped every bill, so Checkout made no
    // payment call at all and looked like it did nothing. `clearSession`
    // normally clears it, but re-ticking the SAME bill in the history table
    // returns early without clearing (customer-history-table's own
    // handleOnClickRow guard), which is exactly the second-payment case.
    //
    // The list is only this flow's if it still has something left to pay, it
    // covers exactly the bills being paid now, and each target still
    // reconciles with what that bill currently owes plus what this flow has
    // already tendered against it (the invariant that holds leg to leg:
    // effectiveDue drops by each payment as halfPaymentAmount rises by it).
    const belongsToThisPaymentFlow =
      paymentHalfDetails.length === payableBills.length &&
      paymentHalfDetails.some(
        (hp) => hp.fullpaymentAmount > hp.halfPaymentAmount
      ) &&
      payableBills.every((bill) => {
        const half = paymentHalfDetails.find((hp) => hp.billId === bill.id);
        if (!half) return false;
        return (
          Math.abs(
            effectiveDueForBill(bill) +
              half.halfPaymentAmount -
              half.fullpaymentAmount
          ) < 0.01
        );
      });

    if (!belongsToThisPaymentFlow) {
      // Resolve each payable bill's itemIds up-front, BEFORE the payment
      // call, using the same hashId -> billItemId lookup handlePickupSubmit
      // uses. When "Pay Full Invoice Now" is on, `fullpaymentAmount` below
      // (effectiveDueForBill) is pinned to the bill's FULL outstanding
      // balance, not just the selected items' share - so itemIds must cover
      // ALL of the bill's items too, or the amount and its item-attribution
      // decouple (see resolveBillItemIds' "all" scope doc above for why:
      // a deselected item could otherwise be left looking permanently
      // unpaid even though its share was actually collected in this
      // payment). Otherwise (payFullNow off), keep the existing
      // selected-only scoping so a partial-selection payment stays scoped
      // to exactly those items.
      let resolutionFailed = false;
      const initialHalfPayments: IHalfPayment[] = payableBills.map((bill) => {
        let itemIds: number[] = [];
        try {
          itemIds = resolveBillItemIds(
            bill,
            invoiceUi[bill.id]?.payFullNow ? "all" : "selected"
          );
        } catch (e: any) {
          resolutionFailed = true;
          snackbar.showSnackbar(e.message, "error");
        }
        return {
          fullpaymentAmount: effectiveDueForBill(bill),
          halfPaymentAmount: 0,
          billId: bill.id,
          customerId: Number(customerId),
          payments: [],
          itemIds,
        };
      });

      if (resolutionFailed) return;

      dispatch(CartSliceAction.setHalfPayementDetails(initialHalfPayments));

      makePayment(
        data.paymentMode,
        data.paidAmount,
        Number(customerId),
        initialHalfPayments,
        data.checkNumber
      );
    } else {
      makePayment(
        data.paymentMode,
        data.paidAmount,
        Number(customerId),
        paymentHalfDetails,
        data.checkNumber
      );
    }
  };

  const makePayment = async (
    paymentType: PaymentTypeEnum,
    totalPaid: number,
    customerId: number,
    halfPaymentProp: IHalfPayment[],
    checkNumber?: string
  ) => {
    let remaining = totalPaid;
    let anyPaymentMade = false;

    for (const bill of halfPaymentProp) {
      if (remaining <= 0) break;

      const billPayableAmount = bill.fullpaymentAmount - bill.halfPaymentAmount;

      if (bill.fullpaymentAmount <= bill.halfPaymentAmount) continue;

      // INTENTIONAL overpayment guard for this UI (investigated/decided under
      // PV-TASK-49, mirrors the identical pattern in payment-modal.tsx's own
      // makePayment - the two payment surfaces are deliberately consistent):
      // `totalPaid`/`remaining` is whatever the cashier TENDERED (e.g. cash
      // handed over, or a card/cheque/store-credit amount typed in), which
      // can legitimately exceed what's actually due. This Math.min caps the
      // amount that ever gets SENT to the backend at the bill's own
      // remaining due, so neither PaymentServiceImpl.validateBillPaymentNotExceedingBalance
      // (the backend's bill-level guard, PV-TASK-36, used when
      // `bill.itemIds` is empty) nor validateItemScopedPayment's own
      // overpayment check (the item-scoped guard, PV-TASK-48, used when
      // `bill.itemIds` is populated - see `paymentRequest.itemIds` below)
      // ever sees an over-the-limit amount from this caller under normal
      // use, for the same reason in both cases - that guard is correct
      // defense-in-depth for other callers (direct API access, races,
      // future UI), not dead code, but this UI itself is not expected
      // to ever trigger it. What happens to the "excess" (tendered minus
      // due) differs deliberately by payment method, and is each method's
      // own responsibility, NOT this loop's:
      //   - CASH: the excess is real physical change handed back to the
      //     customer - CashSection's own "Balance" field shows it live, nothing
      //     further needs to happen here.
      //   - CARD/CHECK: `payment.tsx`'s `isOverpayingWithoutStoreCredit` blocks
      //     checkout entirely unless the cashier explicitly opts in to "Save
      //     balance to store credits", which records the excess via a
      //     separate `addStoreCreditAction` dispatch before this loop runs.
      //   - STORE_CREDIT/MONTHLY/LATER: entry is capped/fixed at the due
      //     amount by their own section components, so `remaining` should
      //     never exceed `billPayableAmount` here in the first place.
      // See qa/tests/settle-monthly-balance/settle-monthly-balance.spec.ts's
      // TC-CAP-01 for a regression test asserting this capping behaviour
      // holds (a bill is settled at exactly its due when more is tendered).
      const paymentAmount = Math.min(
        remaining,
        Number(billPayableAmount.toFixed(2))
      );

      const paymentRequest: IPaymentRequest = {
        paymentType: paymentType,
        billId: bill.billId!,
        customerId: customerId,
        cashierId: user?.id ?? 0,
        amount: Number(paymentAmount.toFixed(2)),
        itemIds: bill.itemIds,
      };

      await updateHalfOrFullPayment(paymentRequest, {
        paymentMode: paymentType,
        paidAmount: paymentAmount,
        checkNumber: checkNumber,
      });

      anyPaymentMade = true;
      remaining -= paymentAmount;
    }

    // Never let Checkout fall through silently: if every bill in the list was
    // skipped there is nothing to collect, and the cashier needs to be told
    // rather than left pressing a button that appears dead.
    if (!anyPaymentMade) {
      snackbar.showSnackbar(
        "Nothing left to pay on the selected invoice(s)",
        "warning"
      );
    }
  };

  const updateHalfOrFullPayment = async (
    paymentRequest: IPaymentRequest,
    paymentModalData: IPaymentModalData
  ) => {
    // `makePayment` awaits this per-bill, in a loop, for multi-session
    // pickups - without this `return`, the outer `async` had no `await` in
    // its body, so it resolved as soon as the thunk was merely dispatched
    // (not once the request/state-update actually finished), firing every
    // bill's payment concurrently instead of one at a time (mirrors
    // payment-modal.tsx's updateHalfOrFullPayment).
    return dispatch(
      newPaymentAction({
        paymentType: paymentModalData.paymentMode,
        billId: paymentRequest.billId,
        customerId: paymentRequest.customerId,
        cashierId: user?.id ?? 0,
        amount: Number(paymentModalData.paidAmount.toFixed(2)), // to fix floating-point precision errors due to js IEEE 754 standard
        itemIds: paymentRequest.itemIds,
        chequeIdentification: paymentModalData.checkNumber,
      })
    ).then(async (res) => {
      const payload = res?.payload as BaseResponseSingle<PaymentCreateResponse>;

      // If the API returned a FAILED status (e.g. the bill was settled out
      // from under this modal, or a backend validation guard like the
      // bill-level overpayment check rejected it), show the error modal with
      // the context we have instead of falling through to any payload.body.*
      // access below - mirrors payment-modal.tsx's updateHalfOrFullPayment.
      if (!payload || payload.status === "FAILED" || !payload.body) {
        const custName = `${customer?.firstName ?? ""} ${
          customer?.lastName ?? ""
        }`.trim();
        setPaymentErrorData({
          paymentType: paymentModalData.paymentMode,
          billAmount: paymentRequest.amount,
          errorMessage: payload?.message ?? undefined,
          tenderedAmount:
            paymentModalData.paymentMode === PaymentTypeEnum.CASH
              ? paymentModalData.paidAmount
              : undefined,
          checkNumber: paymentModalData.checkNumber,
          outstandingAmount:
            store.getState().customer.customerOutstanding?.outstandingAmount,
          availableCredit:
            paymentModalData.paymentMode === PaymentTypeEnum.STORE_CREDIT
              ? customer?.storeCreditBalance
              : undefined,
          customerName: custName,
          customerId: customer?.id,
        });
        setShowPaymentErrorModal(true);
        return;
      }

      // MONTHLY/LATER are invoice-terms, not money-in-hand - the backend
      // deliberately leaves Bill.paidAmount/paymentFilterStatus untouched for
      // these (see PaymentServiceImpl.processPaymentBillUpdate). Bumping the
      // local paidAmount here for them would make trueDueForBill/
      // effectiveDueForBill read 0, wrongly disabling "Pay Full Invoice Now"
      // and blocking the cashier from settling the same bill via a real
      // payment method in the same session.
      const isClearedNow =
        paymentModalData.paymentMode !== PaymentTypeEnum.MONTHLY &&
        paymentModalData.paymentMode !== PaymentTypeEnum.LATER;

      if (isClearedNow) {
        setPreparedData((prev) =>
          prev.map((bill) =>
            bill.id === payload.body.billId
              ? {
                  ...bill,
                  paidAmount: (bill.paidAmount ?? 0) + payload.body.amount,
                  paymentFilterStatus:
                    (bill.paidAmount ?? 0) + payload.body.amount >=
                    bill.billAmount
                      ? BillPaymentStatus.FULLY_PAID
                      : bill.paymentFilterStatus,
                }
              : bill
          )
        );
      }

      const updatedPaymentHalfDetails =
        store.getState().cart.paymentHalfDetails;

      const updatedHalfPayment = JSON.parse(
        JSON.stringify(
          // clone deep gave unexpected behavior so that's why used this method
          updatedPaymentHalfDetails.find(
            (item: IHalfPayment) => item.billId === payload.body.billId
          )
        )
      ) as IHalfPayment;
      if (updatedHalfPayment) {
        updatedHalfPayment.halfPaymentAmount += payload.body.amount;
        updatedHalfPayment.payments.push(payload.body);
        try {
          dispatch(
            CartSliceAction.setHalfPayementDetails([
              updatedHalfPayment,
              ...updatedPaymentHalfDetails.filter(
                (item: IHalfPayment) => item.billId !== payload.body.billId
              ),
            ])
          );
          snackbar.showSnackbar("Payment successful", "success");
        } catch (err: any) {
          snackbar.showSnackbar(`Failed to update payment`, "error");
        }
      } else {
        snackbar.showSnackbar(
          "Failed to find bill's saved half payment",
          "error"
        );
      }
    });
  };

  if (!customerId) {
    snackbar.showSnackbar("Customer ID not found", "error");
    onClose();
    return <></>;
  }

  return (
    <>
    <CustomModal
      title=""
      openModal={open}
      onClose={closeModal}
      mode="popup"
      hideCloseButton
      width={pxToRem(1235)}
      height={pxToRem(900)}
      sx={{
        py: 0,
        px: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderRadius: "1.5rem",
      }}
    >
      {modalType === "pickup" && (
        <>
          <ModalTopBar
            title="Pickup"
            onClose={closeModal}
            subtitle={
              <>
                <b className="text-[#4D4D4D]">{preparedData.length}</b>{" "}
                invoice{preparedData.length === 1 ? "" : "s"} selected ·{" "}
                {billsWithSelection.length} in this pickup ·{" "}
                <b className="text-[#4D4D4D]">{piecesToday}</b> pieces going
                home today
              </>
            }
          />

          <div
            ref={setDrawerHost}
            className="relative flex-1 overflow-y-auto bg-[#F5F6F7] p-6"
          >
            {billItemIdListLoading && (
              <CircularProgress
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                }}
              />
            )}
            {!billItemIdListLoading &&
              preparedData.map((bill) => {
                const ui = invoiceUi[bill.id];
                if (!ui) return null;
                return (
                  <InvoiceCard
                    key={bill.id}
                    bill={bill}
                    ui={ui}
                    paidItemIds={paidItemIdsFor(bill.id)}
                    cardTotal={
                      getSessionTotalBySession(bill) -
                      getSessionDiscountBySession(bill)
                    }
                    dueForSelected={effectiveDueForBill(bill)}
                    trueDueForBill={trueDueForBill(bill)}
                    onToggleExpand={() => toggleExpand(bill.id)}
                    onHeaderToggle={(checked) =>
                      headerToggle(bill.id, checked)
                    }
                    onTogglePartial={() => togglePartialMode(bill.id)}
                    onTogglePayFull={() => togglePayFullNow(bill.id)}
                    onTogglePiece={(itemId, pieceIndex) =>
                      togglePieceSelect(bill.id, itemId, pieceIndex)
                    }
                  />
                );
              })}
            {!billItemIdListLoading && preparedData.length === 0 && (
              <div className="flex h-full items-center justify-center text-[#4D4D4D]/60">
                No invoices selected
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4 border-t border-[#D9D9D9] bg-[#F8F8F8] p-6">
            <div className="flex flex-col justify-center rounded-2xl border border-[#D9D9D9] bg-white p-4 shadow-sm">
              <div className="flex justify-between py-1 text-[length:var(--fs-base)] text-[#4D4D4D]/70">
                <span>Subtotal</span>
                <span className="font-semibold text-[#4D4D4D]">
                  {formatCurrency({ amount: subtotal, prefix: "$" })}
                </span>
              </div>
              <div className="flex justify-between py-1 text-[length:var(--fs-base)] text-[#4D4D4D]/70">
                <span>Discounts</span>
                <span className="font-semibold text-[#4D4D4D]">
                  −{formatCurrency({ amount: totalDiscounts, prefix: "$" })}
                </span>
              </div>
              <div className="flex justify-between py-1 text-[length:var(--fs-base)] text-[#4D4D4D]/70">
                <span>Paid so far</span>
                <span className="font-semibold text-[#018752]">
                  {formatCurrency({ amount: totalPaid, prefix: "$" })}
                </span>
              </div>
              <div className="mt-2 flex items-center justify-between border-t-2 border-[#4D4D4D] pt-2">
                <span className="text-[length:var(--fs-lg)] font-bold text-[#4D4D4D]">
                  Amount Due
                </span>
                <span className="text-[length:var(--fs-lg)] font-bold text-[#018752]">
                  {formatCurrency({
                    amount: amountDue,
                    prefix: "$",
                  })}
                </span>
              </div>
            </div>

            <div className="flex flex-col justify-between gap-3">
              <div className="flex items-center gap-4 rounded-xl border border-[#D9D9D9] bg-white px-4 py-3">
                <span className="whitespace-nowrap text-[length:var(--fs-base)] font-semibold text-[#4D4D4D]/70">
                  Pieces going home today
                </span>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-[#D9D9D9]">
                  <div
                    className="h-full rounded-full bg-[#018752] transition-all"
                    style={{
                      width: `${
                        piecesRemainingTotal
                          ? (piecesToday / piecesRemainingTotal) * 100
                          : 0
                      }%`,
                    }}
                  />
                </div>
                <span className="whitespace-nowrap text-[length:var(--fs-base)] font-bold text-[#4D4D4D]">
                  {piecesToday} / {piecesRemainingTotal}
                </span>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setManagerModalOpen(true)}
                  aria-label="Apply discount"
                  title="Apply discount"
                  className="flex h-[5.857rem] w-[5.857rem] flex-none items-center justify-center rounded-[0.714rem] border border-[#0079C2] bg-white text-[#0079C2] transition hover:bg-[#0079C2]/5"
                >
                  <Percent />
                </button>
                <button
                  type="button"
                  onClick={onSubmit}
                  disabled={ctaDisabled}
                  className="flex h-[5.857rem] flex-1 items-center justify-between gap-2 rounded-[0.714rem] bg-[#018752] px-5 text-[length:var(--fs-lg)] font-bold text-white transition hover:brightness-95 disabled:bg-[#D9D9D9] disabled:text-[#8A8A8A]"
                >
                  <span className="whitespace-nowrap">{ctaLabel}</span>
                  <span className="whitespace-nowrap">
                    {formatCurrency({
                      amount: amountDue,
                      prefix: "$",
                    })}
                  </span>
                </button>
              </div>
            </div>
          </div>

          <ManagerCodeModal
            open={managerModalOpen}
            onClose={() => setManagerModalOpen(false)}
            title="Manager Approval"
            task="open Discount Drawer"
            onDone={() => {
              setManagerModalOpen(false);
              setDiscountDrawerOpen(true);
            }}
            actionType="DISCOUNT_DRAWER_ACCESS"
            requestedByUserId={user?.id}
            billId={
              billsWithSelection.length === 1
                ? billsWithSelection[0].id
                : undefined
            }
          />
          <DiscountDrawer
            isOpen={discountDrawerOpen}
            toggleDrawer={toggleDiscountDrawer}
            containerRef={drawerHost}
            bills={billsWithSelection.map((bill) => ({
              id: bill.id,
              subtotal: bill.billAmount + persistedDiscountFor(bill),
            }))}
          />
        </>
      )}
      {modalType === "payment" && (
        <>
          <ModalTopBar title="Payment" onClose={closeModal} />
          <div className="flex-1 overflow-y-auto p-6">
            <Payment
              onCheckout={handleOnPaymentSubmit}
              onPaymentTypeChange={(type) => {
                setPaymentType(type);
              }}
              paymentType={paymentType}
              pickupAmount={amountDue}
              billId={
                billsWithSelection.length === 1
                  ? billsWithSelection[0].id
                  : undefined
              }
            />
          </div>
        </>
      )}
    </CustomModal>
    <PickupReceiptModal
      open={showPickupReceipt}
      onDone={closePickupReceipt}
      bills={pickupReceiptBills}
      customerName={formatFullName(customer)}
      customerId={customerId}
      customerStatus={customerStatusLetter(customer?.customerTypePref)}
      paymentStatus={paymentStatusLetter(customer?.customerPayAtPref)}
      cashierName={`${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim()}
      cashierId={user?.id ? String(user.id) : undefined}
      paidBy={lastPaymentTypeRef.current ?? undefined}
    />
    {cashChangeData && (
      <CashChangeModal
        open={showCashChangeModal}
        onClose={() => setShowCashChangeModal(false)}
        paymentType={cashChangeData.paymentType}
        billAmount={cashChangeData.billAmount}
        tenderedAmount={cashChangeData.tenderedAmount}
        changeAmount={cashChangeData.changeAmount}
        isStoreCredit={cashChangeData.isStoreCredit}
        checkNumber={cashChangeData.checkNumber}
        outstandingAmount={cashChangeData.outstandingAmount}
        previousStoreCreditBalance={cashChangeData.previousStoreCreditBalance}
        customerName={`${customer?.firstName ?? ""} ${customer?.lastName ?? ""}`.trim()}
        customerId={customerId ? Number(customerId) : undefined}
      />
    )}
    {paymentErrorData && (
      <PaymentErrorModal
        open={showPaymentErrorModal}
        onClose={() => {
          setShowPaymentErrorModal(false);
          setPaymentErrorData(null);
        }}
        onRetry={() => {
          setShowPaymentErrorModal(false);
          setPaymentErrorData(null);
        }}
        {...paymentErrorData}
      />
    )}
    </>
  );
};

export default PickupModal;
